import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { ObservableArray, Subscribable } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import { NodeTracker } from '../../managers/nodes/tracker';
import { withEffect } from '../../mixins/withEffect';
import { IOverlayVM } from './overlay.interfaces';
import { IOverlayButton, OverlayButtonTypes } from './overlayButton.interfaces';
import './overlayStage.html';
import { ExtendedOverlayStageVMConfig, IOverlayStageVM } from './overlayStage.interfaces';
import './overlayStage.scss';

const $document = $(document);

@injectable()
export default class OverlayStageVM<T = object> implements IOverlayStageVM {
    private element: HTMLElement
    private overlay: IOverlayVM<T>

    private isFullscreen: boolean
    private isRemoved = false

    private onOpenHandler: (stage: OverlayStageVM<T>) => void
    private onCloseHandler: (stage: OverlayStageVM<T>) => void

    contentNode = ko.observable<HTMLElement>();

    isActive = ko.observable(false);
    isScrollable = ko.observable(false);
    isBusy = ko.observable(false);

    template: Subscribable<string | undefined>
    templateId: Subscribable<string | undefined>

    data: object
    buttons: ObservableArray<IOverlayButton>    

    isLocked: boolean

    shouldAnimate: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) public config: ExtendedOverlayStageVMConfig<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.element = info.element as HTMLElement;
        this.overlay = config.overlay;

        this.isFullscreen = config.fullscreen ?? false;
        this.isLocked = config.lock ?? false;

        const isStretched = ko.flattenComputed(config.stretch, false);

        this.template = ko.pureComputed(() => _.isString(config.template) ? config.template : undefined);
        this.templateId = ko.pureComputed(() => _.isObject(config.template) ? config.template.id : undefined);

        this.buttons = ko.observableArray(this.buildButtons(config.buttons));

        this.onOpenHandler = config.onOpen ?? _.noop;
        this.onCloseHandler = config.onClose ?? _.noop;

        this.data = _.isFunction(config.data) ? config.data(this) : config.data ?? {};

        this.shouldAnimate = ko.pureComputed(() => config.animate !== false);

        if (!this.isFullscreen) {
            effects.register(node => {
                if (node != undefined) {
                    const updateFunc = () => this.update(node);
                    const { width, height } = new NodeTracker({ node });

                    updateFunc();

                    return [
                        withEffect(updateFunc, [width, height]),
                        withEffect(updateFunc, [this.overlay.width, this.overlay.height])
                    ]
                }
            }, [this.contentNode]);
        }

        if (!this.isLocked) {
            effects.register(isActive => {
                if (isActive) {
                    const func = (e: JQueryEventObject) => {
                        if (e.keyCode === 27)
                            this.close();
                    }

                    $document.on('keydown', func);

                    return () => $document.off('keydown', func as any);
                }
            }, [this.isActive]);
        }

        ko.applyBindingsToNode(info.element, {
            'click': (m: any, e: JQueryEventObject) => this.onClick(e),

            'css': {
                'wcc-overlay__stage': true,
                'wcc-overlay__stage--fullscreen': this.isFullscreen,
                'wcc-overlay__stage--stretch': isStretched,
                'wcc-overlay__stage--inactive': this.isActive.not(),
                'wcc-overlay__stage--scrollable': this.isScrollable
            }
        });
    }

    close() {
        if (!this.isRemoved) {
            this.isRemoved = true;

            this.overlay.removeStage(this);
            this.onCloseHandler(this);
        }
    }

    init() {
        this.onOpenHandler(this);
    }

    private update(node: HTMLElement) {
        const { width, height } = node.getBoundingClientRect();

        if (width > 0 && height > 0 && this.overlay.width() > 0) {
            this.isScrollable(height > this.overlay.height());
        }
    }

    private buildButtons(types?: Array<string>) {
        return (types ?? [])
            .map(type => this.buildButton(type))
            .filter(button => button != undefined) as Array<IOverlayButton>;
    }

    private buildButton(type: string): IOverlayButton | undefined {
        switch (type as OverlayButtonTypes) {
            case 'close':
                return {
                    type: 'close',
                    css: 'wcc-overlay__close-control',
                    click: () => this.close()
                }
        }
    }

    private onClick(e: JQueryEventObject) {
        if (!this.isLocked && e.target === this.element)
            this.close();

        return true;
    }
}